import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import LoadMask from '../../../Utils/LoadMask';
import { useVendorManagementApi } from '../hooks/useVendorManagementApi';

function VendorModal(props) {
   const { vendor, onClose, onSave, loading } = props;

   const [errors, setErrors] = useState({});
   const [liveryVendors, setLiveryVendors] = useState([]);
   const [existingVendorIds, setExistingVendorIds] = useState([]);
   const [loadingVendors, setLoadingVendors] = useState(false);
   const [selectedLiveryVendor, setSelectedLiveryVendor] = useState(null);

   const { getLiveryVendors, getVendors } = useVendorManagementApi();

   const validate = () => {
      const newErrors = {};

      if (!vendor && !selectedLiveryVendor) {
         newErrors.vendor = 'Please select a vendor.';
      }

      if (vendor) {
         if (!name.trim()) {
            newErrors.name = 'Company name is required.';
         }

         if (email.trim() && !/^\S+@\S+\.\S+$/.test(email)) {
            newErrors.email = 'Please enter a valid email address.';
         }
      }

      setErrors(newErrors);

      return Object.keys(newErrors).length === 0;
   };

   var [name, setName] = useState('');
   var [email, setEmail] = useState('');
   var [active, setActive] = useState(true);

   // Fetch livery vendors on mount (for add mode only)
   useEffect(() => {
      if (!vendor) {
         fetchVendorOptions();
      }
   }, []);

   const fetchVendorOptions = async () => {
      setLoadingVendors(true);
      try {
         // Fetch all livery vendors from connectcenter
         const liveryData = await getLiveryVendors();

         // Fetch all existing vendors to get their livery_agreement_ids
         // We need to get ALL vendors, not just one page
         const existingData = await getVendors({ page: 1, pageSize: 1000 });
         const existingIds = (existingData.results || [])
            .filter(v => v.livery_agreement_id)
            .map(v => v.livery_agreement_id);

         setExistingVendorIds(existingIds);
         setLiveryVendors(liveryData || []);
      } catch (error) {
         console.error('Error fetching vendor options:', error);
         setLiveryVendors([]);
      } finally {
         setLoadingVendors(false);
      }
   };

   // Filter out vendors that already exist in our system
   const availableVendors = liveryVendors.filter(
      (v) => !existingVendorIds.includes(v.id)
   );

   // Format options for react-select
   const vendorOptions = availableVendors.map((v) => ({
      value: v.id,
      label: v.name,
      email: v.email,
      phone: v.phone,
   }));

   useEffect(
      function () {
         if (vendor) {
            setName(vendor.name);
            setEmail(vendor.contact_email);
            setActive(vendor.active);
         } else {
            setName('');
            setEmail('');
            setActive(true);
            setSelectedLiveryVendor(null);
         }
      },
      [vendor]
   );

   const handleVendorSelect = (option) => {
      setSelectedLiveryVendor(option);
      if (option) {
         setName(option.label);
         setEmail(option.email || '');
      } else {
         setName('');
         setEmail('');
      }
      setErrors({ ...errors, vendor: null });
   };

   const handleSave = () => {
      if (!validate()) return;

      // Update existing vendor
      if (vendor) {
         const body = {
            name: name.trim(),
            active: active,
         };
         if (email.trim()) body.contact_email = email.trim();
         onSave(vendor.id, body);
         return;
      }

      // Add new vendor from Livery selection
      const body = {
         name: selectedLiveryVendor.label.trim(),
         livery_agreement_id: selectedLiveryVendor.value,
         active: active,
      };
      if (selectedLiveryVendor.email) body.contact_email = selectedLiveryVendor.email.trim();
      onSave(body);
   };

   // Custom filter for react-select to search by name
   const filterOption = (option, inputValue) => {
      if (!inputValue) return true;
      return option.label.toLowerCase().includes(inputValue.toLowerCase());
   };

   return (
      <div className="modal fade show" style={{ display: 'block' }}>
         <div className="modal-dialog">
            <div className="modal-content">
               <LoadMask loading={loading || loadingVendors} dark blur>
                  <div className="modal-header">
                     <h5 className="modal-title">{vendor ? 'Edit Vendor' : 'Add New Vendor'}</h5>
                     <button className="close" onClick={onClose}>
                        <span className="close-content" style={{ fontWeight: '700 !important' }}>
                           &times;
                        </span>
                     </button>
                  </div>

                  <div className="modal-body">
                     {!vendor ? (
                        // Add mode - show searchable dropdown
                        <div className="form-group">
                              <label>
                                 Select Vendor <span className="required">*</span>
                              </label>
                              <Select
                                 options={vendorOptions}
                                 value={selectedLiveryVendor}
                                 onChange={handleVendorSelect}
                                 placeholder="Search for a vendor..."
                                 isClearable
                                 isSearchable
                                 filterOption={filterOption}
                                 noOptionsMessage={() =>
                                    loadingVendors
                                       ? 'Loading vendors...'
                                       : 'No vendors available'
                                 }
                                 styles={{
                                    control: (base, state) => ({
                                       ...base,
                                       borderColor: errors.vendor ? '#dc3545' : base.borderColor,
                                       '&:hover': {
                                          borderColor: errors.vendor ? '#dc3545' : base.borderColor,
                                       },
                                    }),
                                 }}
                              />
                              {errors.vendor && (
                                 <div className="text-danger small mt-1">{errors.vendor}</div>
                              )}
                              {selectedLiveryVendor && selectedLiveryVendor.email && (
                                 <div className="text-muted small mt-2">
                                    Email: {selectedLiveryVendor.email}
                                 </div>
                              )}
                           </div>
                     ) : (
                        // Edit mode - show regular form fields
                        <React.Fragment>
                           <div className="form-group">
                              <label>
                                 Company Name <span className="required">*</span>
                              </label>
                              <input
                                 className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                 value={name}
                                 onChange={(e) => {
                                    setName(e.target.value);
                                    setErrors({ ...errors, name: null });
                                 }}
                              />
                              {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                           </div>

                           <div className="form-group">
                              <label>
                                 Contact Email
                              </label>
                              <input
                                 type="email"
                                 className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                 value={email}
                                 onChange={function (e) {
                                    setEmail(e.target.value);
                                    setErrors({ ...errors, email: null });
                                 }}
                              />
                              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                           </div>
                        </React.Fragment>
                     )}

                     <div className="form-group d-flex align-items-center justify-content-between">
                        <label className="mb-0">Active Status</label>
                        <label className="vendors__toggle-switch mb-0">
                           <input
                              type="checkbox"
                              id="vendor-active"
                              checked={active}
                              onChange={function () {
                                 setActive(!active);
                              }}
                           />
                           <span className="vendors__toggle-slider"></span>
                        </label>
                     </div>
                  </div>

                  <div className="modal-footer">
                     <button className="btn btn-outline-secondary" onClick={onClose}>
                        Cancel
                     </button>
                     <button
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={!vendor && !selectedLiveryVendor}
                     >
                        <i className="fa fa-check mr-1"></i>
                        Save Vendor
                     </button>
                  </div>
               </LoadMask>
            </div>
         </div>

         <div className="modal-backdrop fade show"></div>
      </div>
   );
}

export default VendorModal;
